import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularLoader,
  EmptyState,
  GroupIcon,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@forge/common';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import {
  listPendingJoinRequests,
  approveJoinRequest,
  denyJoinRequest,
} from '@/api/groups';
import type { PendingJoinRequest } from '@/api/groups';

const initials = (first: string, last: string) =>
  `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();

const formatWhen = (iso: string) => {
  const date = new Date(iso);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface JoinRequestsTabProps {
  onDecided?: () => void;
}

/** Admin view: lists pending group-join requests with approve / deny actions. */
const JoinRequestsTab = ({ onDecided }: JoinRequestsTabProps) => {
  const query = useApiQuery<{ requests: PendingJoinRequest[] }>({
    queryFn: (signal) => listPendingJoinRequests(signal),
    mockData: { requests: [] },
  });

  const requests = query.data?.requests ?? [];

  const handleDecided = () => {
    query.reload();
    onDecided?.();
  };

  const approveMutation = useApiMutation<string, unknown>({
    mutationFn: (id) => approveJoinRequest(id),
    successMessage: 'Request approved — membership granted',
    errorMessage: 'Failed to approve request',
    onSuccess: handleDecided,
  });

  const denyMutation = useApiMutation<string, unknown>({
    mutationFn: (id) => denyJoinRequest(id),
    successMessage: 'Request denied',
    errorMessage: 'Failed to deny request',
    onSuccess: handleDecided,
  });

  const isBusy = approveMutation.isSubmitting || denyMutation.isSubmitting;

  return (
    <Card>
      <CardHeader
        title="Group Join Requests"
        subtitle="Review and act on pending membership requests"
      />
      <CardContent>
        {query.isLoading && requests.length === 0 ? (
          <div className="flex justify-center py-10">
            <CircularLoader size="large" />
          </div>
        ) : query.error && requests.length === 0 ? (
          <Banner
            variant="inline"
            type="error"
            title="Failed to load join requests"
            description={query.error}
            canClose={false}
          />
        ) : requests.length === 0 ? (
          <EmptyState
            icon={<GroupIcon />}
            description="No group membership requests are awaiting approval."
          />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Requester</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id}>
                  <TableCell>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar size="sm" text={initials(req.firstName, req.lastName)} />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {req.firstName} {req.lastName}
                        </p>
                        <p className="text-xs text-color-tertiary dark:text-color-tertiary-dark truncate">
                          {req.email}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge color="blue">{req.groupName}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-color-secondary dark:text-color-secondary-dark">
                      {req.requestedAt ? formatWhen(req.requestedAt) : '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-color-tertiary dark:text-color-tertiary-dark line-clamp-2">
                      {req.note || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="destructive-secondary"
                        disabled={isBusy}
                        onClick={() => denyMutation.mutate(req.id)}
                      >
                        Deny
                      </Button>
                      <Button
                        variant="primary"
                        disabled={isBusy}
                        onClick={() => approveMutation.mutate(req.id)}
                      >
                        Approve
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default JoinRequestsTab;
